<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { NCard, NSpace, NButton, NLog, NTag, NPopconfirm, NText, NTooltip } from 'naive-ui'
import { deployments, streams, type Service } from '../api'
import { message } from '../message'

export type OpKind = 'deploy' | 'start' | 'stop' | 'restart' | 'rollback'
export type OpStatus = 'running' | 'success' | 'failed' | 'cancelled'

export interface OpView {
  kind: OpKind
  id?: number             // deployment_id for deploy/rollback only
  phase: string           // current phase key (matches a step.key)
  status: OpStatus
  error?: string
  statusText: string
  startedAt: number
  endedAt?: number
}

const props = defineProps<{ op: OpView; service: Service }>()
const emit  = defineEmits<{ dismiss: []; cancelled: [] }>()

interface StepDef { key: string; zh: string; en: string; tip: string }

const STEPS_BY_KIND: Record<OpKind, StepDef[]> = {
  deploy:   [
    { key: 'pre',          zh: '准备',     en: 'Pre',     tip: '执行 pre_deploy 钩子脚本（如数据库迁移），超时时间由服务配置决定' },
    { key: 'pulling',      zh: '拉取镜像', en: 'Pull',    tip: '从镜像仓库拉取指定版本的 Docker 镜像到本地' },
    { key: 'swapping',     zh: '替换容器', en: 'Swap',    tip: '停止并移除旧容器，用新镜像启动新容器 — 此为流量切换点' },
    { key: 'probing',      zh: '健康检查', en: 'Probe',   tip: '向容器发送 HTTP 健康检查请求，最多重试 3 次、每次间隔 2 秒' },
    { key: 'proxy',        zh: '更新代理', en: 'Proxy',   tip: '更新 Caddy 反向代理配置并重载，将域名指向新容器端口' },
    { key: 'post',         zh: '收尾',     en: 'Post',    tip: '执行 post_deploy 钩子脚本（如清理缓存），失败不影响部署结果' },
    { key: 'rolling_back', zh: '自动回滚', en: 'Rollback',tip: '当前步骤失败，正在自动切回上一个部署成功的版本' },
    { key: 'done',         zh: '完成',     en: 'Done',    tip: '' },
  ],
  rollback: [
    { key: 'pre',          zh: '准备',     en: 'Pre',     tip: '执行 pre_deploy 钩子脚本（如数据库迁移），超时时间由服务配置决定' },
    { key: 'pulling',      zh: '拉取镜像', en: 'Pull',    tip: '从镜像仓库拉取指定版本的 Docker 镜像到本地' },
    { key: 'swapping',     zh: '替换容器', en: 'Swap',    tip: '停止并移除旧容器，用新镜像启动新容器 — 此为流量切换点' },
    { key: 'probing',      zh: '健康检查', en: 'Probe',   tip: '向容器发送 HTTP 健康检查请求，最多重试 3 次、每次间隔 2 秒' },
    { key: 'proxy',        zh: '更新代理', en: 'Proxy',   tip: '更新 Caddy 反向代理配置并重载，将域名指向新容器端口' },
    { key: 'post',         zh: '收尾',     en: 'Post',    tip: '执行 post_deploy 钩子脚本（如清理缓存），失败不影响部署结果' },
    { key: 'rolling_back', zh: '自动回滚', en: 'Rollback',tip: '当前步骤失败，正在自动切回上一个部署成功的版本' },
    { key: 'done',         zh: '完成',     en: 'Done',    tip: '' },
  ],
  start:    [
    { key: 'starting', zh: '启动中', en: 'Starting', tip: '执行 docker start 启动已有容器，不重新拉取镜像' },
    { key: 'done',     zh: '完成',   en: 'Done',     tip: '' },
  ],
  stop:     [
    { key: 'stopping', zh: '停止中', en: 'Stopping', tip: '执行 docker stop 停止运行中的容器（10 秒优雅停止超时）' },
    { key: 'done',     zh: '完成',   en: 'Done',     tip: '' },
  ],
  restart:  [
    { key: 'stopping', zh: '停止中', en: 'Stopping', tip: '执行 docker stop 停止运行中的容器（10 秒优雅停止超时）' },
    { key: 'starting', zh: '启动中', en: 'Starting', tip: '执行 docker start 启动已有容器，不重新拉取镜像' },
    { key: 'done',     zh: '完成',   en: 'Done',     tip: '' },
  ],
}

type StepState = 'pending' | 'active' | 'done' | 'failed' | 'cancelled' | 'skipped'

const steps = computed(() => STEPS_BY_KIND[props.op.kind])
const hasHealth = computed(() => !!props.service.health_path)

function stepState(i: number): StepState {
  const op = props.op
  const step = steps.value[i]
  const isLast = i === steps.value.length - 1

  // Probe is skipped when the service declares no health_path.
  // Applies to deploy and rollback (their step lists include 'probing').
  if (step.key === 'probing' && !hasHealth.value) {
    if (op.status === 'running' && op.phase === 'probing') return 'active'
    return 'skipped'
  }
  // Pre / Proxy / Post are skipped when the service doesn't configure them.
  if (step.key === 'pre' && !props.service.pre_deploy_cmd) {
    if (op.status === 'running' && op.phase === 'pre') return 'active'
    return 'skipped'
  }
  if (step.key === 'proxy' && !props.service.domain) {
    if (op.status === 'running' && op.phase === 'proxy') return 'active'
    return 'skipped'
  }
  if (step.key === 'post' && !props.service.post_deploy_cmd) {
    if (op.status === 'running' && op.phase === 'post') return 'active'
    return 'skipped'
  }

  if (isLast) {
    if (op.status === 'success')   return 'done'
    if (op.status === 'failed')    return 'failed'
    if (op.status === 'cancelled') return 'cancelled'
    return 'pending'
  }

  // Non-terminal step.
  if (op.status === 'failed' || op.status === 'cancelled') {
    // Mark the step that was active when the op terminated.
    if (op.phase === step.key) return op.status === 'cancelled' ? 'cancelled' : 'failed'
    const phaseIdx = steps.value.findIndex(s => s.key === op.phase)
    const myIdx = i
    if (phaseIdx === -1) return 'pending'
    return myIdx < phaseIdx ? 'done' : 'pending'
  }
  if (op.status === 'success') return 'done'
  if (op.phase === step.key) return 'active'
  const phaseIdx = steps.value.findIndex(s => s.key === op.phase)
  if (phaseIdx === -1) return 'pending'
  return i < phaseIdx ? 'done' : 'pending'
}

function stepColor(s: StepState): string {
  switch (s) {
    case 'active':    return '#2080f0'
    case 'done':      return '#18a058'
    case 'failed':    return '#d03050'
    case 'cancelled': return '#f0a020'
    case 'skipped':   return '#d0d0d0'
    default:          return '#bbb'
  }
}
function stepGlyph(s: StepState): string {
  switch (s) {
    case 'done':      return '✓'
    case 'failed':    return '✗'
    case 'cancelled': return '⊘'
    case 'skipped':   return '—'
    case 'active':    return '●'
    default:          return '○'
  }
}

function stepTip(step: StepDef): string {
  if (step.key === 'done') {
    switch (props.op.status) {
      case 'success':   return '操作已成功完成'
      case 'failed':    return '操作执行失败，详见下方错误信息'
      case 'cancelled': return '操作已被取消'
      default:          return '等待前置步骤完成…'
    }
  }
  return step.tip
}

const tagType = computed(() => {
  switch (props.op.status) {
    case 'running':   return 'info'
    case 'success':   return 'success'
    case 'cancelled': return 'warning'
    default:          return 'error'
  }
})
const tagText = computed(() => {
  const k = props.op.kind
  const verb = k === 'deploy' ? '部署' : k === 'rollback' ? '回滚' :
               k === 'start' ? '启动' : k === 'stop' ? '停止' : '重启'
  switch (props.op.status) {
    case 'running':   return `${verb}中`
    case 'success':   return `${verb}成功`
    case 'cancelled': return `${verb}已取消`
    default:          return `${verb}失败`
  }
})

const supportsLog   = computed(() => props.op.kind === 'deploy' || props.op.kind === 'rollback')
const supportsCancel = computed(() => (props.op.kind === 'deploy' || props.op.kind === 'rollback') && props.op.status === 'running')

// --- log panel (deploy/rollback only) ---
const showLog = ref(false)
const logBuf = ref('')
const probeAttempt = ref<{ n: number; max: number } | null>(null)
let src: EventSource | null = null

function openStream(id: number) {
  closeStream()
  logBuf.value = ''
  src = new EventSource(streams.deployLog(id))
  src.onmessage = (m) => {
    logBuf.value += m.data + '\n'
    const match = m.data.match(/\[probe\] attempt (\d+):/)
    if (match) probeAttempt.value = { n: parseInt(match[1], 10), max: 3 }
  }
  src.onerror = () => { closeStream() }
}
function closeStream() {
  src?.close()
  src = null
  probeAttempt.value = null
}
onBeforeUnmount(closeStream)

watch(showLog, (v) => {
  if (v && props.op.id) openStream(props.op.id)
  else closeStream()
})
watch(() => props.op.id, (newId) => {
  if (showLog.value && newId) openStream(newId)
})

// Auto-expand log on terminal failure/cancelled (for kinds that have a log).
watch(() => props.op.status, (s, prev) => {
  if (prev === 'running' && supportsLog.value &&
      (s === 'failed' || s === 'cancelled')) {
    showLog.value = true
  }
})

async function onCancel() {
  if (!props.op.id) return
  try {
    await deployments.cancel(props.op.id)
    message.success('已取消')
    emit('cancelled')
  } catch (e: any) {
    message.error(e.message)
  }
}
</script>

<template>
  <NCard size="small" style="margin-top:12px" :embedded="true">
    <NSpace align="center" justify="space-between" style="width:100%">
      <NSpace align="center" :size="8">
        <template v-for="(s, i) in steps" :key="s.key">
          <NTooltip trigger="hover" :disabled="!stepTip(s)">
            <template #trigger>
              <div style="display:flex;flex-direction:column;align-items:center;min-width:64px;cursor:default">
                <div :style="`width:28px;height:28px;border-radius:50%;background:${stepColor(stepState(i))};color:white;display:flex;align-items:center;justify-content:center;font-weight:600`">
                  {{ stepGlyph(stepState(i)) }}
                </div>
                <NText :depth="stepState(i) === 'pending' ? 3 : 1" style="font-size:12px;margin-top:4px">
                  {{ s.zh }}
                </NText>
                <NText depth="3" style="font-size:10px">{{ s.en }}{{ stepState(i) === 'skipped' ? '（跳过）' : '' }}</NText>
                <NText v-if="s.key === 'probing' && stepState(i) === 'active' && probeAttempt" depth="3" style="font-size:10px">
                  {{ probeAttempt.n }}/{{ probeAttempt.max }}
                </NText>
              </div>
            </template>
            <span>{{ stepTip(s) }}</span>
          </NTooltip>
          <div v-if="i < steps.length - 1" :style="`width:24px;height:2px;background:${stepState(i) === 'done' ? '#18a058' : '#e0e0e0'}`" />
        </template>
      </NSpace>

      <NSpace>
        <NTag :type="tagType" size="small">{{ tagText }}</NTag>

        <NButton v-if="supportsLog" size="small" tertiary @click="showLog = !showLog">
          {{ showLog ? '收起日志' : '展开日志' }}
        </NButton>

        <NPopconfirm v-if="supportsCancel" @positive-click="onCancel">
          <template #trigger>
            <NButton size="small" type="warning">中断</NButton>
          </template>
          中断当前部署？这只会在记录中标记为失败，不会回滚容器或撤销已拉取的镜像。
        </NPopconfirm>

        <NButton v-if="op.status !== 'running'" size="small" tertiary @click="emit('dismiss')">×</NButton>
      </NSpace>
    </NSpace>

    <div style="margin-top:8px;font-size:13px">
      {{ op.statusText }}
    </div>
    <div v-if="op.error" style="margin-top:4px;font-size:13px;color:#d03050">
      {{ op.error }}
    </div>

    <div v-if="showLog && supportsLog" style="margin-top:8px">
      <NLog :log="logBuf || '（等待日志输出…）'" :rows="14" trim />
    </div>
  </NCard>
</template>
